import configService from '@/services/config';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface StreamEvent {
  type: 'meta' | 'tool' | 'delta' | 'error' | 'done';
  model?: string;
  name?: string;
  text?: string;
  error?: string;
}

/**
 * Streaming version using fetch + SSE parsing. Calls `onEvent` for each parsed event.
 * Returns a function to cancel mid-stream.
 */
export function streamAdvisor(
  messages: ChatMessage[],
  params: any,
  breakdown: any,
  model: string,
  onEvent: (evt: StreamEvent) => void,
): () => void {
  const controller = new AbortController();
  (async () => {
    try {
      const token = sessionStorage.getItem('access_token') || '';
      const tenantId = sessionStorage.getItem('tenant_id') || '';
      // Resolve the absolute backend URL from configService — same source
      // the axios `api` instance uses. A relative `/api/v1/...` path here
      // hits the frontend's static origin on deployed environments
      // (Cloud Run separates frontend and backend), and the static server
      // returns "HTTP 405 Method Not Allowed" for POST → surfaced as the
      // "Advisor unavailable: HTTP 405" toast in the cost-simulator chat.
      const apiBase = configService.getApiBaseUrl();
      const res = await fetch(`${apiBase}/cost-simulator/agent/chat?stream=1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-ID': tenantId,
        },
        body: JSON.stringify({ messages, params, breakdown, model }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        onEvent({ type: 'error', error: `HTTP ${res.status}` });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const chunk = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (chunk.startsWith('data: ')) {
            try {
              const data = JSON.parse(chunk.slice(6));
              onEvent(data);
              if (data.type === 'done') return;
            } catch {
              // ignore parse error
            }
          }
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        onEvent({ type: 'error', error: e?.message ?? String(e) });
      }
    }
  })();
  return () => controller.abort();
}
